/**
 * SOLID Review
 * S — Strong. One concern: the table's video chat — turning it on and off, the idle rule, the server's removal, the
 *     banner, and reporting each kind of media use once per room.
 * O — Good. A new reason to turn video off is one more move over `turnedOff`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Good. The factories, the per-room state the screen reads, the session part, and one method per move.
 * D — Strong. The account value object and the schemas' types; the builder's part role.
 */
import { Account } from '../session/Account';
import type { IStateClientSessionPart } from '../decisions/ClientDecision';
import type { IPropsClientInput } from '../ports/schemas/ClientChanges';
import type { IPropsClientSessionChanges } from '../ports/schemas/ClientSession';
import {
  NO_MEDIA, type IEntityClientMedia, type ICommandMediaIdle, type ICommandMediaUsed, type IPayloadMediaPresence,
} from '../ports/schemas/MediaMoves';

/**
 * THE TABLE'S VIDEO CHAT, AS A SMALL AGGREGATE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide
 * functions of FE5c (`decisions/media/mediaPolicy.ts`), moved from useLiveKitSession. The state is per ROOM: one kept
 * for another room (or none) means nothing at this one, and reads as fresh (`stateIn`). A move answers whether it
 * applied; every move but the presence report needs a table — the connection is per room. Only a move writes the
 * session: a fresh state read for another room is not kept by the reading.
 */
export class MediaPolicy implements IStateClientSessionPart {
  private moved = false;

  private constructor(
    private media: IEntityClientMedia,
    /** The table's last move: the idle clock's other start. */
    private readonly lastMoveAt: number,
  ) {}

  /**
   * The media state for `roomId`: the kept one if it is that room's, else a fresh one — the hook reset everything
   * when the room went. The screen reads the current table's through this (FE5c).
   */
  static stateIn(kept: IEntityClientMedia, roomId: string | null): IEntityClientMedia {
    return kept.roomId === roomId ? kept : { ...NO_MEDIA, roomId };
  }

  /** The video chat of the table the session sits at — none at no table. */
  static atTable(input: IPropsClientInput): MediaPolicy {
    return MediaPolicy.inRoom(input, input.table.room?.roomId ?? null);
  }

  /** The session's video chat in room `roomId`, the one a use was seen in. */
  static inRoom(input: IPropsClientInput, roomId: string | null): MediaPolicy {
    return new MediaPolicy(MediaPolicy.stateIn(input.session.media, roomId), input.table.lastGameActivityAt);
  }

  private atTable(): boolean {
    return this.media.roomId !== null;
  }

  /** The room's state as a move leaves it: the session keeps it. */
  private keep(media: IEntityClientMedia): boolean {
    this.media = media;
    this.moved = true;
    return true;
  }

  /** Turned off by something other than the player: the banner offers the reconnect. */
  private turnedOff(): boolean {
    const off: IEntityClientMedia = { ...this.media, enabled: false, autoDisconnected: true, idleBannerDismissed: false };
    return this.keep(off);
  }

  /** Turned on: the idle clock starts now, and an earlier automatic disconnect is over. */
  private turnedOn(now: number): boolean {
    const on: IEntityClientMedia = { ...this.media, enabled: true, enabledAt: now, autoDisconnected: false };
    return this.keep(on);
  }

  /** The 📡 control (`toggleLkConnection`): off if on, on if off. */
  toggled(now: number): boolean {
    if (!this.atTable()) return false;
    if (!this.media.enabled) return this.turnedOn(now);
    const off: IEntityClientMedia = { ...this.media, enabled: false };
    return this.keep(off);
  }

  /** The idle banner's or badge's Reconnect (`reconnectLk`). */
  reconnected(now: number): boolean {
    return this.atTable() && this.turnedOn(now);
  }

  /**
   * The idle rule (§23.7): a connection left open with no gameplay for the timeout is turned off, to stop accruing
   * connection-time cost. Activity is the table's last move or the moment video was turned on, whichever is later —
   * the hook's clock, which every move and every (re)enable reset.
   */
  idleChecked(command: ICommandMediaIdle): boolean {
    if (!this.atTable() || !this.media.enabled) return false;
    const lastActivity = Math.max(this.lastMoveAt, this.media.enabledAt ?? 0);
    if (command.now - lastActivity < command.idleTimeoutMs) return false;
    return this.turnedOff();
  }

  /**
   * The server's idle sweep removed this player ON PURPOSE (§21.10: the whole table was idle). Rejoining by itself
   * would undo that, so it is the local idle rule's outcome: off, banner up, one tap reconnects.
   */
  removedByServer(): boolean {
    return this.atTable() && this.turnedOff();
  }

  /** The idle banner's ✕ (dismissed: the badge stays) — or a fresh disconnect surfacing it again. */
  bannerDismissed(dismissed: boolean): boolean {
    if (!this.atTable()) return false;
    const banner: IEntityClientMedia = { ...this.media, idleBannerDismissed: dismissed };
    return this.keep(banner);
  }

  /**
   * The player's first use of the microphone or the camera in this room, to report — once per kind per room, marked
   * before the call as the hook's refs were — or none. Guests are never reported (data minimisation: the server
   * discards their calls anyway, so the round-trip is skipped).
   */
  used(command: ICommandMediaUsed, by: Account): IPayloadMediaPresence | null {
    if (by.isGuest()) return null;
    const video = command.kind === 'video';
    if (video ? this.media.reportedVideo : this.media.reportedAudio) return null;
    const reported: IEntityClientMedia = video ? { ...this.media, reportedVideo: true } : { ...this.media, reportedAudio: true };
    this.keep(reported);
    const presence: IPayloadMediaPresence = { roomId: command.roomId, usedVideo: video, usedAudio: !video };
    return presence;
  }

  /** The room's state if a move kept it; nothing otherwise. */
  sessionFields(): IPropsClientSessionChanges {
    const fields: IPropsClientSessionChanges = this.moved ? { media: this.media } : {};
    return fields;
  }
}
